import { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import {
  LEAD_EVENT_TYPES,
  RENTALS_TYPES,
  type LeadEventType,
  type RentalsType,
  type ProjectWithConfig,
  type RentalsTypeWithConfig,
} from "../shared/types";
import { getEventTypeLabel, getEventTypeDescription } from "../shared/utils";
import { VariableHelpTooltip } from "../shared/VariableHelpTooltip";
import {
  SMARTHOME_ACTIONS,
  type SmarthomeAction,
  type SmarthomeEventMapping,
  getSmarthomeActionLabel,
} from "./types";

const SmartHomeRentalsCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);
  const [selectedPipelines, setSelectedPipelines] = useState<Record<string, string>>({});
  const [defaultModuleIds, setDefaultModuleIds] = useState<Record<string, string>>({});
  const [pipelines, setPipelines] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eventMappings, setEventMappings] = useState<Record<string, SmarthomeEventMapping[]>>({});
  const [stages, setStages] = useState<Record<string, Array<{ id: string; name: string }>>>({});
  const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>({});
  const [selectedTransferCategories, setSelectedTransferCategories] = useState<
    Record<string, string[]>
  >({});

  const { data: projects, isLoading: isLoadingProjects } = trpc.crmSelfConfig.listProjects.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && merchantType === "development" }
  );

  const { data: crmConfigurations, isLoading: isLoadingConfigurations } =
    trpc.crmSelfConfig.listCrmConfigurations.useQuery(
      { merchantId: merchantId || "" },
      { enabled: !!merchantId }
    );

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const { data: agents } = trpc.agents.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const transferCategories = useMemo(() => {
    if (!agents) return [];
    const categoriesSet = new Set<string>();
    agents.forEach((agent) => {
      if (agent.config?.transferConversationCategories) {
        agent.config.transferConversationCategories.forEach((category) => {
          categoriesSet.add(category.name);
        });
      }
    });
    return Array.from(categoriesSet);
  }, [agents]);

  const projectsWithConfig = useMemo(() => {
    if (merchantType !== "development" || !projects || !crmConfigurations) return [];

    return projects.map((project) => {
      const config = crmConfigurations.find((c) => c.projectId === project.id);
      return {
        id: project.id,
        name: project.name,
        crmConfiguration: config
          ? {
              id: config.id,
              pipelineId: config.smarthomeRentals?.pipelineId,
              pipelineName: config.smarthomeRentals?.pipelineName,
              defaultModuleId: config.smarthomeRentals?.defaultModuleId,
              smarthomeEventsMapping: (config.smarthomeEventsMapping ||
                []) as SmarthomeEventMapping[],
            }
          : undefined,
      };
    });
  }, [projects, crmConfigurations, merchantType]);

  const rentalsTypesWithConfig = useMemo(() => {
    if (merchantType !== "rentals" || !crmConfigurations) return [];

    return RENTALS_TYPES.map((rentalsType) => {
      const config = crmConfigurations.find((c) => c.rentalsType === rentalsType.id);
      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config
          ? {
              id: config.id,
              pipelineId: config.smarthomeRentals?.pipelineId,
              pipelineName: config.smarthomeRentals?.pipelineName,
              defaultModuleId: config.smarthomeRentals?.defaultModuleId,
              smarthomeEventsMapping: (config.smarthomeEventsMapping ||
                []) as SmarthomeEventMapping[],
              transferCategories: config.transferCategories as string[] | undefined,
            }
          : undefined,
      };
    });
  }, [crmConfigurations, merchantType]);

  useEffect(() => {
    if (merchantType === "development" && projectsWithConfig.length > 0) {
      const initialPipelines: Record<string, string> = {};
      const initialModuleIds: Record<string, string> = {};
      const initialMappings: Record<string, SmarthomeEventMapping[]> = {};
      projectsWithConfig.forEach((project) => {
        if (project.crmConfiguration?.pipelineId) {
          initialPipelines[project.id] = String(project.crmConfiguration.pipelineId);
        }
        if (project.crmConfiguration?.defaultModuleId) {
          initialModuleIds[project.id] = project.crmConfiguration.defaultModuleId;
        }
        if (project.crmConfiguration?.smarthomeEventsMapping) {
          initialMappings[project.id] = project.crmConfiguration.smarthomeEventsMapping;
        }
      });
      setSelectedPipelines(initialPipelines);
      setDefaultModuleIds(initialModuleIds);
      setEventMappings(initialMappings);
    } else if (merchantType === "rentals" && rentalsTypesWithConfig.length > 0) {
      const initialPipelines: Record<string, string> = {};
      const initialModuleIds: Record<string, string> = {};
      const initialMappings: Record<string, SmarthomeEventMapping[]> = {};
      const initialTransferCategories: Record<string, string[]> = {};
      rentalsTypesWithConfig.forEach((rentalsType) => {
        if (rentalsType.crmConfiguration?.pipelineId) {
          initialPipelines[rentalsType.id] = String(rentalsType.crmConfiguration.pipelineId);
        }
        if (rentalsType.crmConfiguration?.defaultModuleId) {
          initialModuleIds[rentalsType.id] = rentalsType.crmConfiguration.defaultModuleId;
        }
        if (rentalsType.crmConfiguration?.smarthomeEventsMapping) {
          initialMappings[rentalsType.id] = rentalsType.crmConfiguration.smarthomeEventsMapping;
        }
        if (rentalsType.crmConfiguration?.transferCategories) {
          initialTransferCategories[rentalsType.id] =
            rentalsType.crmConfiguration.transferCategories;
        }
      });
      setSelectedPipelines(initialPipelines);
      setDefaultModuleIds(initialModuleIds);
      setEventMappings(initialMappings);
      setSelectedTransferCategories(initialTransferCategories);
    }
  }, [projectsWithConfig, rentalsTypesWithConfig, merchantType]);

  const utils = trpc.useUtils();
  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración del proyecto se ha creado correctamente.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración del proyecto se ha actualizado correctamente.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  const getPipelines = trpc.crmSelfConfig.getPipelines.useMutation({
    onSuccess: (result) => {
      if (result.success && result.pipelines) {
        setPipelines(
          result.pipelines.map((p) => ({
            id: String(p.id),
            name: p.name,
          }))
        );
        setLoadingPipelines(false);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los pipelines.",
        });
        setLoadingPipelines(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los pipelines.",
      });
      setLoadingPipelines(false);
    },
  });

  const getStages = trpc.crmSelfConfig.getStages.useMutation();

  const handleLoadStages = useCallback(
    async (id: string, pipelineId: string) => {
      if (!crmConnection?.provider || !crmConnection.smarthomeRentals?.companyCode) {
        toast.error("Error", {
          description: "No hay conexión CRM configurada.",
        });
        return;
      }

      if (loadingStages[id] || stages[id]) {
        return;
      }

      setLoadingStages((prev) => ({ ...prev, [id]: true }));

      try {
        const result = await getStages.mutateAsync({
          provider: crmConnection.provider,
          companyCode: crmConnection.smarthomeRentals.companyCode,
          pipelineId,
        });

        if (result.success && result.stages) {
          setStages((prev) => ({
            ...prev,
            [id]:
              result.stages?.map((s) => ({
                id: String(s.id),
                name: s.name,
              })) || [],
          }));
        } else {
          toast.error("Error", {
            description: result.error || "No se pudieron obtener las etapas.",
          });
        }
      } catch (error: any) {
        toast.error("Error", {
          description: error.message || "Ocurrió un error al obtener las etapas.",
        });
      } finally {
        setLoadingStages((prev) => ({ ...prev, [id]: false }));
      }
    },
    [crmConnection, getStages, loadingStages, stages]
  );

  const handleLoadPipelines = async () => {
    if (!crmConnection?.provider || !crmConnection.smarthomeRentals?.companyCode) {
      toast.error("Error", {
        description: "No hay conexión CRM configurada.",
      });
      return;
    }

    setLoadingPipelines(true);
    try {
      await getPipelines.mutateAsync({
        provider: crmConnection.provider,
        companyCode: crmConnection.smarthomeRentals.companyCode,
      });
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleEdit = async (id: string) => {
    if (pipelines.length === 0) {
      await handleLoadPipelines();
    }
    setEditingId(id);

    if (crmConnection?.provider) {
      const item =
        merchantType === "development"
          ? projectsWithConfig.find((p) => p.id === id)
          : rentalsTypesWithConfig.find((r) => r.id === id);
      const pipelineId = item?.crmConfiguration?.pipelineId || selectedPipelines[id];
      if (pipelineId && !stages[id]) {
        await handleLoadStages(id, String(pipelineId));
      }
    }
  };

  const handlePipelineChange = (id: string, pipelineId: string) => {
    setSelectedPipelines((prev) => ({ ...prev, [id]: pipelineId }));
    setStages((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleAddEventType = (id: string, eventType: LeadEventType) => {
    const currentMappings = eventMappings[id] || [];
    if (currentMappings.some((m) => m.event === eventType)) {
      return;
    }
    setEventMappings((prev) => ({
      ...prev,
      [id]: [...currentMappings, { event: eventType }],
    }));
  };

  const handleRemoveEventType = (id: string, eventType: LeadEventType) => {
    setEventMappings((prev) => ({
      ...prev,
      [id]: (prev[id] || []).filter((m) => m.event !== eventType),
    }));
  };

  const handleAddAction = (id: string, eventType: LeadEventType, action: SmarthomeAction) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const existingMapping = mappings.find((m) => m.event === eventType);

      if (!existingMapping) {
        if (action === "createLead") {
          return {
            ...prev,
            [id]: [
              ...mappings,
              {
                event: eventType,
                createLead: {
                  projectId: "",
                  stageId: "",
                  origin: "",
                  comment: "",
                },
              },
            ],
          };
        }
        return prev;
      }

      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType) {
          const updated = { ...m };

          if (action === "createLead") {
            if (m.createLead) {
              return m;
            }
            updated.createLead = {
              projectId: "",
              stageId: "",
              origin: "",
              comment: "",
            };
          } else if (action === "postEvent") {
            if (m.createLead) {
              return m;
            }
            updated.postEvent = {
              eventContent: "",
              userId: "",
            };
          } else if (action === "changeUserAssigned") {
            updated.changeUserAssigned = {};
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleRemoveAction = (id: string, eventType: LeadEventType, action: SmarthomeAction) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType) {
          const updated = { ...m } as any;
          if (action === "createLead") {
            delete updated.createLead;
          } else if (action === "postEvent") {
            delete updated.postEvent;
          } else if (action === "changeUserAssigned") {
            delete updated.changeUserAssigned;
            if (updated.createLead) {
              delete updated.createLead.changeUserAssigned;
            }
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleUpdateActionField = (
    id: string,
    eventType: LeadEventType,
    action: SmarthomeAction,
    field: string,
    value: any
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType) {
          const updated = { ...m } as any;
          if (action === "createLead" && updated.createLead) {
            if (field === "postEvent") {
              updated.createLead.postEvent = value;
            } else if (field === "changeUserAssigned") {
              updated.createLead.changeUserAssigned = value;
            } else {
              updated.createLead = { ...updated.createLead, [field]: value };
            }
          } else if (action === "postEvent" && updated.postEvent) {
            updated.postEvent = { ...updated.postEvent, [field]: value };
          } else if (action === "changeUserAssigned" && updated.changeUserAssigned) {
            updated.changeUserAssigned = { ...updated.changeUserAssigned, ...value };
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleSaveConfiguration = async (item: ProjectWithConfig | RentalsTypeWithConfig) => {
    if (!merchantId || !crmConnection?.provider) return;

    // Validate that location sources are configured
    if (
      !crmConnection.smarthomeRentals?.locationSources?.default ||
      crmConnection.smarthomeRentals.locationSources.default.trim() === ""
    ) {
      toast.error("Error", {
        description:
          "Por favor configura las Variables Globales antes de guardar la configuración. La fuente de ubicación 'Default' es obligatoria.",
      });
      return;
    }

    const selectedPipelineId = selectedPipelines[item.id];
    if (!selectedPipelineId) {
      toast.error("Error", {
        description: "Por favor selecciona un pipeline.",
      });
      return;
    }

    const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);
    if (!selectedPipeline) return;

    const defaultModuleId = defaultModuleIds[item.id];
    if (!defaultModuleId || defaultModuleId.trim() === "") {
      toast.error("Error", {
        description: "Por favor ingresa el ID del módulo (inmueble) por defecto.",
      });
      return;
    }

    const mappings = eventMappings[item.id] || [];

    // Clean up changeUserAssigned to only include checked categories with valid userIds
    const cleanedMappings = mappings.map((mapping) => {
      const cleaned = { ...mapping } as any;

      // Clean standalone changeUserAssigned
      if (cleaned.changeUserAssigned) {
        // Check if it's an empty object
        if (Object.keys(cleaned.changeUserAssigned).length === 0) {
          cleaned.changeUserAssigned = undefined;
        } else {
          const filtered: Record<string, string> = {};
          Object.entries(cleaned.changeUserAssigned).forEach(([key, value]) => {
            if (value && typeof value === "string" && value.trim() !== "") {
              filtered[key] = value;
            }
          });
          // Only keep if there are valid entries, otherwise remove it
          cleaned.changeUserAssigned = Object.keys(filtered).length > 0 ? filtered : undefined;
        }
      }

      // Clean changeUserAssigned inside createLead
      if (cleaned.createLead?.changeUserAssigned) {
        const changeUserAssigned = cleaned.createLead.changeUserAssigned;
        const transferCategories = changeUserAssigned.transferCategories || {};
        const defaultUserId = changeUserAssigned.defaultUserId;

        // Filter transferCategories to only include valid entries
        const filteredCategories: Record<string, string> = {};
        Object.entries(transferCategories).forEach(([key, value]) => {
          if (value && typeof value === "string" && value.trim() !== "") {
            filteredCategories[key] = value;
          }
        });

        // Build the new structure
        const newChangeUserAssigned: {
          transferCategories?: Record<string, string>;
          defaultUserId?: string;
        } = {};

        if (Object.keys(filteredCategories).length > 0) {
          newChangeUserAssigned.transferCategories = filteredCategories;
        }
        if (defaultUserId && typeof defaultUserId === "string" && defaultUserId.trim() !== "") {
          newChangeUserAssigned.defaultUserId = defaultUserId;
        }

        // Only keep if there are valid entries, otherwise remove it
        cleaned.createLead.changeUserAssigned =
          Object.keys(newChangeUserAssigned).length > 0 ? newChangeUserAssigned : undefined;
      }

      return cleaned;
    });

    // Validate all required fields
    for (const mapping of cleanedMappings) {
      const eventLabel = getEventTypeLabel(mapping.event);

      if (mapping.createLead) {
        if (!mapping.createLead.projectId || mapping.createLead.projectId.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Crear Lead" requiere un Project ID.`,
          });
          return;
        }
        if (!mapping.createLead.stageId || mapping.createLead.stageId.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Crear Lead" requiere un Stage ID.`,
          });
          return;
        }
        if (!mapping.createLead.comment || mapping.createLead.comment.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Crear Lead" requiere un comentario.`,
          });
          return;
        }
        if (mapping.createLead.postEvent) {
          if (
            !mapping.createLead.postEvent.eventContent ||
            mapping.createLead.postEvent.eventContent.trim() === ""
          ) {
            toast.error("Error", {
              description: `Para el evento "${eventLabel}", el postEvent dentro de "Crear Lead" requiere un contenido de evento.`,
            });
            return;
          }
          if (
            !mapping.createLead.postEvent.userId ||
            mapping.createLead.postEvent.userId.trim() === ""
          ) {
            toast.error("Error", {
              description: `Para el evento "${eventLabel}", el postEvent dentro de "Crear Lead" requiere un User ID.`,
            });
            return;
          }
        }
      }

      if (mapping.postEvent) {
        if (!mapping.postEvent.eventContent || mapping.postEvent.eventContent.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Post Event" requiere un contenido de evento.`,
          });
          return;
        }
        if (!mapping.postEvent.userId || mapping.postEvent.userId.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Post Event" requiere un User ID.`,
          });
          return;
        }
      }
    }

    const smarthomeMappings: SmarthomeEventMapping[] = cleanedMappings;

    // Debug logging
    console.log("Saving smarthomeMappings:", JSON.stringify(smarthomeMappings, null, 2));

    smarthomeMappings.forEach((mapping) => {
      if (mapping.changeUserAssigned) {
        console.log("changeUserAssigned in mapping:", mapping.changeUserAssigned);
        console.log("Keys:", Object.keys(mapping.changeUserAssigned));
        console.log("Values:", Object.values(mapping.changeUserAssigned));
      }
      if (mapping.createLead?.changeUserAssigned) {
        console.log("changeUserAssigned in createLead:", mapping.createLead.changeUserAssigned);
        console.log("Keys:", Object.keys(mapping.createLead.changeUserAssigned));
        console.log("Values:", Object.values(mapping.createLead.changeUserAssigned));
      }
    });

    const transferCats =
      item.id === "general" ? selectedTransferCategories[item.id] || [] : undefined;

    if (item.crmConfiguration) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: item.crmConfiguration.id,
        ...(transferCats !== undefined && { transferCategories: transferCats }),
        smarthomeRentals: {
          pipelineId: selectedPipeline.id,
          pipelineName: selectedPipeline.name,
          defaultModuleId,
        },
        smarthomeEventsMapping: smarthomeMappings,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        ...(merchantType === "development" && { projectId: item.id }),
        ...(merchantType === "rentals" && { rentalsType: item.id as RentalsType }),
        ...(transferCats !== undefined && { transferCategories: transferCats }),
        provider: crmConnection.provider,
        enabled: true,
        smarthomeRentals: {
          pipelineId: selectedPipeline.id,
          pipelineName: selectedPipeline.name,
          defaultModuleId,
        },
        smarthomeEventsMapping: smarthomeMappings,
      });
    }
    setEditingId(null);
  };

  const getAvailableEventTypes = (id: string): LeadEventType[] => {
    const currentMappings = eventMappings[id] || [];
    const usedEvents = new Set(currentMappings.map((m) => m.event));
    return LEAD_EVENT_TYPES.filter((event) => !usedEvents.has(event));
  };

  const getMappingsForItem = (id: string): SmarthomeEventMapping[] => {
    return eventMappings[id] || [];
  };

  const getAvailableActions = (id: string, eventType: LeadEventType): SmarthomeAction[] => {
    const mapping = eventMappings[id]?.find((m) => m.event === eventType);
    if (!mapping) {
      return [...SMARTHOME_ACTIONS];
    }

    if (mapping.createLead) {
      return ["createLead"];
    }

    return [...SMARTHOME_ACTIONS];
  };

  const handleChangeUserAssignedCategoryChange = (
    id: string,
    eventType: LeadEventType,
    action: SmarthomeAction | "createLead",
    categoryName: string,
    checked: boolean,
    userId?: string
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType) {
          const updated = { ...m } as any;

          if (action === "changeUserAssigned") {
            // Read current map from state
            const currentMap = (updated.changeUserAssigned || {}) as Record<string, string>;
            // Build new complete map - allow empty strings in state for UI
            const newMap: Record<string, string> = { ...currentMap };

            if (checked) {
              newMap[categoryName] = userId ?? "";
            } else {
              delete newMap[categoryName];
            }

            // Store entire map - keep empty strings in state for UI, will be filtered on save
            updated.changeUserAssigned = Object.keys(newMap).length > 0 ? newMap : undefined;
          } else if (action === "createLead" && updated.createLead) {
            const currentChangeUserAssigned = updated.createLead.changeUserAssigned || {};
            const transferCategories = currentChangeUserAssigned.transferCategories || {};
            const defaultUserId = currentChangeUserAssigned.defaultUserId;

            // Update the category
            const updatedTransferCategories = { ...transferCategories };
            if (checked) {
              updatedTransferCategories[categoryName] = userId ?? "";
            } else {
              delete updatedTransferCategories[categoryName];
            }

            // Build the new structure
            const newChangeUserAssigned: {
              transferCategories?: Record<string, string>;
              defaultUserId?: string;
            } = {};

            if (Object.keys(updatedTransferCategories).length > 0) {
              newChangeUserAssigned.transferCategories = updatedTransferCategories;
            }
            if (defaultUserId) {
              newChangeUserAssigned.defaultUserId = defaultUserId;
            }

            updated.createLead = {
              ...updated.createLead,
              changeUserAssigned:
                Object.keys(newChangeUserAssigned).length > 0 ? newChangeUserAssigned : undefined,
            };
          }

          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleChangeUserAssignedDefaultUserIdChange = (
    id: string,
    eventType: LeadEventType,
    defaultUserId: string
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.createLead) {
          const updated = { ...m } as any;
          const currentChangeUserAssigned = updated.createLead.changeUserAssigned || {};
          const transferCategories = currentChangeUserAssigned.transferCategories || {};

          // Build the new structure
          const newChangeUserAssigned: {
            transferCategories?: Record<string, string>;
            defaultUserId?: string;
          } = {};

          if (Object.keys(transferCategories).length > 0) {
            newChangeUserAssigned.transferCategories = transferCategories;
          }
          if (defaultUserId && defaultUserId.trim() !== "") {
            newChangeUserAssigned.defaultUserId = defaultUserId;
          }

          updated.createLead = {
            ...updated.createLead,
            changeUserAssigned:
              Object.keys(newChangeUserAssigned).length > 0 ? newChangeUserAssigned : undefined,
          };

          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  if (isLoadingConfigurations || (merchantType === "development" && isLoadingProjects)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p>
            {merchantType === "development" ? "Cargando proyectos..." : "Cargando configuración..."}
          </p>
        </div>
      </div>
    );
  }

  const itemsToRender =
    merchantType === "development" ? projectsWithConfig : rentalsTypesWithConfig;

  if (merchantType === "development" && (!projects || projects.length === 0)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">No hay proyectos disponibles.</p>
      </div>
    );
  }

  if (merchantType === "rentals" && rentalsTypesWithConfig.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">No hay tipos de rentals disponibles.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">
            {merchantType === "development"
              ? "Configuración por Proyecto"
              : "Configuración por Tipo de Negocio"}
          </p>
          <p className="text-sm text-gray-600">
            {merchantType === "development"
              ? "Asigna un pipeline a cada proyecto y configura los eventos"
              : "Asigna un pipeline a cada tipo de negocio (Arriendo residencial, Arriendo comercial, Venta) y configura los eventos"}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {itemsToRender.map((item) => {
            const isEditing = editingId === item.id;
            const hasPipeline = !!selectedPipelines[item.id] || !!item.crmConfiguration?.pipelineId;
            const mappings = getMappingsForItem(item.id);
            const availableEventTypes = getAvailableEventTypes(item.id);

            return (
              <div key={item.id} className="rounded-md border border-gray-200 p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">
                      {item.name}
                    </p>
                    <div className="flex items-center gap-2">
                      {item.crmConfiguration ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                          Configurado
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">
                          Sin configurar
                        </Badge>
                      )}
                      {!isEditing && (
                        <Button
                          size="sm"
                          onClick={() => handleEdit(item.id)}
                          disabled={loadingPipelines && pipelines.length === 0}
                        >
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    loadingPipelines && pipelines.length === 0 ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <p className="text-xs text-gray-500">
                          Cargando pipelines...
                        </p>
                      </div>
                    ) : pipelines.length > 0 ? (
                      <>
                        <div className="flex flex-col gap-2">
                          <Label className="text-xs text-gray-600">
                            Pipeline
                          </Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={selectedPipelines[item.id] || ""}
                            onChange={(e) => {
                              const pipelineId = e.target.value;
                              if (pipelineId) {
                                handlePipelineChange(item.id, pipelineId);
                                handleLoadStages(item.id, pipelineId);
                              }
                            }}
                          >
                            <option value="">Selecciona un pipeline</option>
                            {pipelines.map((pipeline) => (
                              <option key={pipeline.id} value={pipeline.id}>
                                {pipeline.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {item.crmConfiguration?.pipelineName && (
                          <p className="text-xs text-gray-500">
                            Pipeline actual: {item.crmConfiguration.pipelineName}
                          </p>
                        )}

                        <div className="mt-4 flex flex-col gap-2">
                          <Label className="text-xs text-gray-600">
                            ID módulo (inmueble) por defecto: <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            className="bg-gray-50 text-sm"
                            value={defaultModuleIds[item.id] || ""}
                            onChange={(e) =>
                              setDefaultModuleIds((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }))
                            }
                            placeholder="Ingresa el ID del módulo por defecto"
                            required
                          />
                        </div>

                        {item.id === "general" && transferCategories.length > 0 && (
                          <div className="mt-4 flex flex-col gap-2">
                            <Label className="text-xs text-gray-600">
                              Categorías de transferencia que enrutan a este pipeline
                            </Label>
                            <p className="text-xs text-gray-400">
                              Cuando un lead tenga una de estas categorías de transferencia, se usará
                              esta configuración de CRM.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {transferCategories.map((category) => {
                                const isSelected =
                                  selectedTransferCategories[item.id]?.includes(category) ?? false;
                                return (
                                  <div key={category} className="flex items-center gap-1.5">
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(checked) => {
                                        setSelectedTransferCategories((prev) => {
                                          const current = prev[item.id] || [];
                                          if (checked) {
                                            return { ...prev, [item.id]: [...current, category] };
                                          }
                                          return {
                                            ...prev,
                                            [item.id]: current.filter((c) => c !== category),
                                          };
                                        });
                                      }}
                                    />
                                    <span className="text-xs">{category}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {hasPipeline && (
                          <div className="mt-4">
                            <p className="mb-3 text-sm font-semibold">
                              Mapeo de Eventos
                            </p>
                            <div className="flex flex-col gap-4">
                              <div>
                                <p className="mb-2 text-xs font-semibold text-gray-600">
                                  Tipos de Eventos
                                </p>
                                {availableEventTypes.length > 0 ? (
                                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                                    {availableEventTypes.map((eventType) => (
                                      <div
                                        key={eventType}
                                        className="rounded-md border border-gray-200 bg-gray-50 p-2"
                                      >
                                        <div className="flex items-center justify-between">
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="flex-1 cursor-help text-xs">
                                                  {getEventTypeLabel(eventType)}
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                {getEventTypeDescription(eventType)}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="size-6"
                                            aria-label="Agregar evento"
                                            onClick={() => handleAddEventType(item.id, eventType)}
                                          >
                                            <Plus className="size-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs italic text-gray-500">
                                    Todos los tipos de eventos han sido agregados
                                  </p>
                                )}
                              </div>

                              {mappings.length > 0 && (
                                <div>
                                  <p className="mb-2 text-xs font-semibold text-gray-600">
                                    Acciones de SmartHome Rentals
                                  </p>
                                  <div className="flex flex-col gap-3">
                                    {mappings.map((mapping) => (
                                      <div
                                        key={mapping.event}
                                        className="rounded-md border border-gray-200 bg-gray-50 p-3"
                                      >
                                        <div className="flex flex-col gap-3">
                                          <div className="flex items-center justify-between">
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <span className="cursor-help text-xs font-semibold text-blue-600">
                                                    {getEventTypeLabel(mapping.event)}
                                                  </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                  {getEventTypeDescription(mapping.event)}
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="size-6 text-red-600 hover:text-red-700"
                                              aria-label="Eliminar evento"
                                              onClick={() =>
                                                handleRemoveEventType(item.id, mapping.event)
                                              }
                                            >
                                              <Trash2 className="size-4" />
                                            </Button>
                                          </div>
                                          <Separator />
                                          <div className="flex flex-col gap-2">
                                            {getAvailableActions(item.id, mapping.event).map(
                                              (action) => {
                                                const hasAction = !!(
                                                  (action === "createLead" && mapping.createLead) ||
                                                  (action === "postEvent" && mapping.postEvent) ||
                                                  (action === "changeUserAssigned" &&
                                                    mapping.changeUserAssigned !== undefined)
                                                );
                                                return (
                                                  <div key={action}>
                                                    <div className={`flex items-center gap-2 ${hasAction ? "mb-2" : ""}`}>
                                                      <Checkbox
                                                        checked={hasAction}
                                                        onCheckedChange={(checked) => {
                                                          if (checked) {
                                                            handleAddAction(
                                                              item.id,
                                                              mapping.event,
                                                              action
                                                            );
                                                          } else {
                                                            handleRemoveAction(
                                                              item.id,
                                                              mapping.event,
                                                              action
                                                            );
                                                          }
                                                        }}
                                                        disabled={
                                                          (action !== "createLead" &&
                                                            !!mapping.createLead) ||
                                                          (action === "changeUserAssigned" &&
                                                            !!mapping.createLead)
                                                        }
                                                      />
                                                      <label className="text-xs">
                                                        {getSmarthomeActionLabel(action)}
                                                      </label>
                                                    </div>
                                                    {hasAction && (
                                                      <div className="ml-6 mt-2">
                                                        {action === "createLead" &&
                                                          mapping.createLead && (
                                                            <div className="flex flex-col gap-2">
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Project ID <span className="text-red-500">*</span>
                                                                </Label>
                                                                <Input
                                                                  className="text-sm"
                                                                  value={
                                                                    mapping.createLead.projectId ||
                                                                    ""
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "projectId",
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                  required
                                                                />
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Stage ID <span className="text-red-500">*</span>
                                                                </Label>
                                                                {loadingStages[item.id] ? (
                                                                  <div className="flex items-center gap-2">
                                                                    <Spinner size="sm" />
                                                                    <p className="text-xs text-gray-500">
                                                                      Cargando etapas...
                                                                    </p>
                                                                  </div>
                                                                ) : stages[item.id] &&
                                                                  stages[item.id].length > 0 ? (
                                                                  <select
                                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                    value={
                                                                      mapping.createLead.stageId ||
                                                                      ""
                                                                    }
                                                                    onChange={(e) => {
                                                                      const selectedStageId =
                                                                        e.target.value;
                                                                      const selectedStage = stages[
                                                                        item.id
                                                                      ]?.find(
                                                                        (s) =>
                                                                          String(s.id) ===
                                                                          String(selectedStageId)
                                                                      );
                                                                      if (selectedStage) {
                                                                        handleUpdateActionField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          action,
                                                                          "stageId",
                                                                          selectedStage.id
                                                                        );
                                                                      }
                                                                    }}
                                                                    required
                                                                  >
                                                                    <option value="">Selecciona una etapa</option>
                                                                    {stages[item.id].map(
                                                                      (stage) => (
                                                                        <option
                                                                          key={stage.id}
                                                                          value={String(stage.id)}
                                                                        >
                                                                          {stage.name}
                                                                        </option>
                                                                      )
                                                                    )}
                                                                  </select>
                                                                ) : selectedPipelines[item.id] ? (
                                                                  <p className="text-xs text-gray-500">
                                                                    No se pudieron cargar las
                                                                    etapas.
                                                                  </p>
                                                                ) : (
                                                                  <p className="text-xs text-gray-500">
                                                                    Selecciona un pipeline primero.
                                                                  </p>
                                                                )}
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Origin (Opcional)
                                                                </Label>
                                                                <Input
                                                                  className="text-sm"
                                                                  value={
                                                                    mapping.createLead.origin || ""
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "origin",
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                />
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Comentario <span className="text-red-500">*</span>
                                                                </Label>
                                                                <Input
                                                                  className="text-sm"
                                                                  value={
                                                                    mapping.createLead.comment || ""
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "comment",
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                  required
                                                                />
                                                                <div className="mt-1 flex items-center">
                                                                  <VariableHelpTooltip />
                                                                </div>
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Post Event (Opcional)
                                                                </Label>
                                                                <div className="flex flex-col gap-2">
                                                                  <Input
                                                                    className="text-sm"
                                                                    placeholder="Event Content"
                                                                    value={
                                                                      mapping.createLead?.postEvent
                                                                        ?.eventContent || ""
                                                                    }
                                                                    onChange={(e) => {
                                                                      const currentPostEvent =
                                                                        mapping.createLead
                                                                          ?.postEvent;
                                                                      handleUpdateActionField(
                                                                        item.id,
                                                                        mapping.event,
                                                                        action,
                                                                        "postEvent",
                                                                        currentPostEvent
                                                                          ? {
                                                                              ...currentPostEvent,
                                                                              eventContent:
                                                                                e.target.value,
                                                                              userId:
                                                                                currentPostEvent.userId,
                                                                            }
                                                                          : {
                                                                              eventContent:
                                                                                e.target.value,
                                                                              userId: "",
                                                                            }
                                                                      );
                                                                    }}
                                                                  />
                                                                  <Input
                                                                    className="text-sm"
                                                                    placeholder="User ID"
                                                                    value={
                                                                      mapping.createLead?.postEvent
                                                                        ?.userId || ""
                                                                    }
                                                                    onChange={(e) => {
                                                                      const currentPostEvent =
                                                                        mapping.createLead
                                                                          ?.postEvent;
                                                                      handleUpdateActionField(
                                                                        item.id,
                                                                        mapping.event,
                                                                        action,
                                                                        "postEvent",
                                                                        currentPostEvent
                                                                          ? {
                                                                              ...currentPostEvent,
                                                                              eventContent:
                                                                                currentPostEvent.eventContent,
                                                                              userId:
                                                                                e.target.value,
                                                                            }
                                                                          : {
                                                                              eventContent: "",
                                                                              userId:
                                                                                e.target.value,
                                                                            }
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>
                                                              </div>
                                                              <div>
                                                                <p className="mb-2 text-xs font-medium">
                                                                  Cambiar usuario asignado
                                                                  (Opcional)
                                                                </p>
                                                                <div className="mb-2 rounded-md border-l-[3px] border-blue-200 bg-blue-50 p-2">
                                                                  <p className="text-xs text-blue-700">
                                                                    Asegúrate de que las categorías
                                                                    seleccionadas estén descritas en
                                                                    las instrucciones del agente
                                                                    para que puedan las
                                                                    conversaciones puedan ser
                                                                    correctamente asignadas a las
                                                                    diferentes categorías
                                                                  </p>
                                                                </div>
                                                                {transferCategories.length === 0 ? (
                                                                  <p className="text-xs text-gray-500">
                                                                    No hay categorías de
                                                                    transferencia configuradas en
                                                                    los agentes.
                                                                  </p>
                                                                ) : (
                                                                  <div className="flex flex-col gap-2">
                                                                    {transferCategories.map(
                                                                      (categoryName) => {
                                                                        const changeUserAssigned =
                                                                          mapping.createLead
                                                                            ?.changeUserAssigned;
                                                                        const transferCategoriesMap =
                                                                          changeUserAssigned?.transferCategories ||
                                                                          {};

                                                                        const isChecked =
                                                                          categoryName in
                                                                          transferCategoriesMap;
                                                                        const userId =
                                                                          transferCategoriesMap[
                                                                            categoryName
                                                                          ] || "";
                                                                        return (
                                                                          <div key={categoryName}>
                                                                            <div className="flex items-center gap-2">
                                                                              <Checkbox
                                                                                checked={isChecked}
                                                                                onCheckedChange={(checked) => {
                                                                                  handleChangeUserAssignedCategoryChange(
                                                                                    item.id,
                                                                                    mapping.event,
                                                                                    "createLead",
                                                                                    categoryName,
                                                                                    checked as boolean,
                                                                                    userId
                                                                                  );
                                                                                }}
                                                                              />
                                                                              <label className="text-xs">
                                                                                {categoryName}
                                                                              </label>
                                                                            </div>
                                                                            {isChecked && (
                                                                              <div className="ml-6 mt-1 w-full max-w-full">
                                                                                <Input
                                                                                  className="text-sm"
                                                                                  placeholder="ID de usuario"
                                                                                  value={userId}
                                                                                  onChange={(e) => {
                                                                                    handleChangeUserAssignedCategoryChange(
                                                                                      item.id,
                                                                                      mapping.event,
                                                                                      "createLead",
                                                                                      categoryName,
                                                                                      true,
                                                                                      e.target.value
                                                                                    );
                                                                                  }}
                                                                                />
                                                                              </div>
                                                                            )}
                                                                          </div>
                                                                        );
                                                                      }
                                                                    )}
                                                                  </div>
                                                                )}
                                                                <div className="mt-4 flex flex-col gap-2">
                                                                  <Label className="text-xs">
                                                                    ID de usuario por defecto
                                                                    (Opcional)
                                                                  </Label>
                                                                  <Input
                                                                    className="text-sm"
                                                                    placeholder="ID de usuario por defecto"
                                                                    value={
                                                                      (mapping.createLead
                                                                        ?.changeUserAssigned &&
                                                                      typeof mapping.createLead
                                                                        .changeUserAssigned ===
                                                                        "object" &&
                                                                      !Array.isArray(
                                                                        mapping.createLead
                                                                          .changeUserAssigned
                                                                      ) &&
                                                                      "defaultUserId" in
                                                                        mapping.createLead
                                                                          .changeUserAssigned
                                                                        ? (
                                                                            mapping.createLead
                                                                              .changeUserAssigned as any
                                                                          ).defaultUserId
                                                                        : "") || ""
                                                                    }
                                                                    onChange={(e) => {
                                                                      handleChangeUserAssignedDefaultUserIdChange(
                                                                        item.id,
                                                                        mapping.event,
                                                                        e.target.value
                                                                      );
                                                                    }}
                                                                  />
                                                                  <p className="mt-1 text-xs text-gray-500">
                                                                    Se usará cuando ninguna
                                                                    categoría coincida con las
                                                                    categorías de transferencia
                                                                  </p>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          )}
                                                        {action === "postEvent" &&
                                                          mapping.postEvent && (
                                                            <div className="flex flex-col gap-2">
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Event Content <span className="text-red-500">*</span>
                                                                </Label>
                                                                <Input
                                                                  className="text-sm"
                                                                  value={
                                                                    mapping.postEvent
                                                                      .eventContent || ""
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "eventContent",
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                  required
                                                                />
                                                                <div className="mt-1 flex items-center">
                                                                  <VariableHelpTooltip />
                                                                </div>
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  User ID <span className="text-red-500">*</span>
                                                                </Label>
                                                                <Input
                                                                  className="text-sm"
                                                                  value={
                                                                    mapping.postEvent.userId || ""
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "userId",
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                  required
                                                                />
                                                              </div>
                                                            </div>
                                                          )}
                                                        {action === "changeUserAssigned" &&
                                                          mapping.changeUserAssigned !==
                                                            undefined && (
                                                            <div className="flex flex-col gap-2">
                                                              <p className="mb-2 text-xs font-medium">
                                                                Categorías de transferencia
                                                              </p>
                                                              <div className="mb-2 rounded-md border-l-[3px] border-blue-200 bg-blue-50 p-2">
                                                                <p className="text-xs text-blue-700">
                                                                  Asegúrate de que las categorías
                                                                  seleccionadas estén descritas en
                                                                  las instrucciones del agente para
                                                                  que puedan las conversaciones
                                                                  puedan ser correctamente asignadas
                                                                  a las diferentes categorías
                                                                </p>
                                                              </div>
                                                              {transferCategories.length === 0 ? (
                                                                <p className="text-xs text-gray-500">
                                                                  No hay categorías de transferencia
                                                                  configuradas en los agentes.
                                                                </p>
                                                              ) : (
                                                                <div className="flex flex-col gap-2">
                                                                  {transferCategories.map(
                                                                    (categoryName) => {
                                                                      const isChecked =
                                                                        categoryName in
                                                                        (mapping.changeUserAssigned ||
                                                                          {});
                                                                      const userId =
                                                                        mapping
                                                                          .changeUserAssigned?.[
                                                                          categoryName
                                                                        ] || "";
                                                                      return (
                                                                        <div key={categoryName}>
                                                                          <div className="flex items-center gap-2">
                                                                            <Checkbox
                                                                              checked={isChecked}
                                                                              onCheckedChange={(checked) => {
                                                                                handleChangeUserAssignedCategoryChange(
                                                                                  item.id,
                                                                                  mapping.event,
                                                                                  action,
                                                                                  categoryName,
                                                                                  checked as boolean,
                                                                                  userId
                                                                                );
                                                                              }}
                                                                            />
                                                                            <label className="text-xs">
                                                                              {categoryName}
                                                                            </label>
                                                                          </div>
                                                                          {isChecked && (
                                                                            <div className="ml-6 mt-1 w-full max-w-full">
                                                                              <Input
                                                                                className="text-sm"
                                                                                placeholder="ID de usuario"
                                                                                value={userId}
                                                                                onChange={(e) => {
                                                                                  handleChangeUserAssignedCategoryChange(
                                                                                    item.id,
                                                                                    mapping.event,
                                                                                    action,
                                                                                    categoryName,
                                                                                    true,
                                                                                    e.target.value
                                                                                  );
                                                                                }}
                                                                              />
                                                                            </div>
                                                                          )}
                                                                        </div>
                                                                      );
                                                                    }
                                                                  )}
                                                                </div>
                                                              )}
                                                            </div>
                                                          )}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              }
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveConfiguration(item)}
                            disabled={
                              createConfiguration.isLoading ||
                              updateConfiguration.isLoading ||
                              !selectedPipelines[item.id] ||
                              !defaultModuleIds[item.id] ||
                              defaultModuleIds[item.id].trim() === ""
                            }
                          >
                            {item.crmConfiguration ? "Actualizar" : "Guardar"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            Cancelar
                          </Button>
                        </div>
                      </>
                    ) : null
                  ) : (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-500">
                        {item.crmConfiguration
                          ? `Pipeline configurado: ${item.crmConfiguration.pipelineName}`
                          : "Haz clic en Editar para configurar el pipeline"}
                      </p>
                      {item.crmConfiguration?.defaultModuleId && (
                        <p className="text-xs text-gray-500">
                          ID módulo (inmueble) por defecto: {item.crmConfiguration.defaultModuleId}
                        </p>
                      )}
                      {item.crmConfiguration?.smarthomeEventsMapping &&
                        item.crmConfiguration.smarthomeEventsMapping.length > 0 && (
                          <p className="text-xs text-gray-500">
                            Eventos:{" "}
                            {item.crmConfiguration.smarthomeEventsMapping
                              .map((m: any) => getEventTypeLabel(m.event))
                              .join(", ")}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SmartHomeRentalsCrmConfiguration;
